// Per-stream receive counters for the WebRTC developer popup.
//
// `inbound-rtp` counters belong to one RTCPeerConnection and restart at zero
// whenever a viewer reconnects. StreamStatsTracker keeps the finished
// connections' totals so the popup can show either the current connection or
// everything since logging started.

export type Counters = {
    bytesReceived: number;
    framesReceived: number;
    framesDecoded: number;
    /** Decoded key frames (`keyFramesDecoded`). */
    iFrames: number;
    /** Decoded frames that were not key frames. */
    pFrames: number;
};

export type InboundVideoSample = Counters & {
    width: number | null;
    height: number | null;
};

export const RATE_METRICS = ["bytesReceived", "framesReceived", "framesDecoded", "iFrames", "pFrames"] as const;
export type RateMetric = (typeof RATE_METRICS)[number];

/** Per-second rates between two consecutive samples of one connection. */
export type RatePoint = { t: number } & Record<RateMetric, number>;

/** Rate history kept per stream: two minutes at one sample per second. */
export const HISTORY_POINTS = 120;

export type StreamStatsSnapshot = {
    connected: boolean;
    current: Counters;
    total: Counters;
    width: number | null;
    height: number | null;
    /** Connections opened after the first one since logging started. */
    reconnects: number;
    history: RatePoint[];
};

const ZERO: Counters = { bytesReceived: 0, framesReceived: 0, framesDecoded: 0, iFrames: 0, pFrames: 0 };

const asCount = (value: unknown): number =>
    typeof value === "number" && Number.isFinite(value) ? value : 0;

const asDimension = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;

/** Reads the video `inbound-rtp` entry of an RTCStatsReport, or null before media flows. */
export const readInboundVideo = (report: RTCStatsReport): InboundVideoSample | null => {
    let entry: Record<string, unknown> | null = null;
    report.forEach((stats: Record<string, unknown>) => {
        if (stats.type === "inbound-rtp" && (stats.kind || stats.mediaType) === "video") entry = stats;
    });
    if (!entry) return null;
    const inbound = entry as Record<string, unknown>;
    const framesDecoded = asCount(inbound.framesDecoded);
    const iFrames = Math.min(asCount(inbound.keyFramesDecoded), framesDecoded);
    return {
        bytesReceived: asCount(inbound.bytesReceived),
        framesReceived: asCount(inbound.framesReceived),
        framesDecoded,
        iFrames,
        pFrames: framesDecoded - iFrames,
        width: asDimension(inbound.frameWidth),
        height: asDimension(inbound.frameHeight),
    };
};

const add = (left: Counters, right: Counters): Counters => ({
    bytesReceived: left.bytesReceived + right.bytesReceived,
    framesReceived: left.framesReceived + right.framesReceived,
    framesDecoded: left.framesDecoded + right.framesDecoded,
    iFrames: left.iFrames + right.iFrames,
    pFrames: left.pFrames + right.pFrames,
});

export class StreamStatsTracker {
    private finished: Counters = ZERO;

    private current: Counters = ZERO;

    private connectionId: string | null = null;

    private connectionsOpened = 0;

    private width: number | null = null;

    private height: number | null = null;

    private history: RatePoint[] = [];

    private previous: { at: number; counters: Counters } | null = null;

    open(connectionId: string): void {
        this.retireCurrent();
        this.connectionId = connectionId;
        this.connectionsOpened += 1;
    }

    close(connectionId: string): void {
        if (connectionId === this.connectionId) this.retireCurrent();
    }

    /** Ignores samples from a connection that is no longer the current one. */
    update(connectionId: string, sample: InboundVideoSample, now: number = Date.now()): void {
        if (connectionId !== this.connectionId) return;
        this.current = {
            bytesReceived: sample.bytesReceived,
            framesReceived: sample.framesReceived,
            framesDecoded: sample.framesDecoded,
            iFrames: sample.iFrames,
            pFrames: sample.pFrames,
        };
        this.recordRate(now);
        this.width = sample.width ?? this.width;
        this.height = sample.height ?? this.height;
    }

    snapshot(): StreamStatsSnapshot {
        return {
            connected: this.connectionId !== null,
            current: this.current,
            total: add(this.finished, this.current),
            width: this.connectionId ? this.width : null,
            height: this.connectionId ? this.height : null,
            reconnects: Math.max(0, this.connectionsOpened - 1),
            history: [...this.history],
        };
    }

    // Counters restart at zero on a new connection, so a rate is only computed
    // between two samples of the same connection.
    private recordRate(now: number): void {
        const previous = this.previous;
        this.previous = { at: now, counters: this.current };
        if (!previous || now <= previous.at) return;
        const seconds = (now - previous.at) / 1000;
        const point = { t: now } as RatePoint;
        RATE_METRICS.forEach((metric) => {
            point[metric] = Math.max(0, (this.current[metric] - previous.counters[metric]) / seconds);
        });
        this.history.push(point);
        if (this.history.length > HISTORY_POINTS) this.history.splice(0, this.history.length - HISTORY_POINTS);
    }

    private retireCurrent(): void {
        this.finished = add(this.finished, this.current);
        this.current = ZERO;
        this.connectionId = null;
        this.previous = null;
    }
}
